import type { ImportSourcePreset } from './types'
import { ModuleTypeEnum } from '../../types/module-type'

/**
 * Clients, projects and tasks from work-management tools.
 *
 * These exports are LIST VIEWS: the columns are whatever the user configured,
 * so the header names below come from each tool's default list labels and
 * help-center screenshots, not a verified sample file (`headersVerified:
 * false`). Detection therefore keys on a column that is characteristic of the
 * tool ("Standing", "Against", "Project No", "Subitems"), and anything a preset
 * does not know falls through to ordinary auto-mapping.
 */

/**
 * monday.com → board menu → Export board to Excel. Boards are fully custom;
 * this maps the column types every board template ships with. Workbooks with
 * the board title and group banner rows are flattened by
 * `locateBoardExportLayout` (group title → "Group").
 */
export const MONDAY_BOARD_TASKS_PRESET: ImportSourcePreset = {
  id: 'monday-board-tasks',
  sourceSystem: 'MONDAY',
  sourceLabel: 'monday.com',
  exportLabel: 'board export',
  moduleType: ModuleTypeEnum.TASK,
  fingerprint: {
    requireAll: [
      ['name', 'item'],
      ['subitems', 'itemid'],
    ],
    distinctive: ['person', 'people', 'owner', 'timeline', 'group'],
  },
  columns: {
    name: 'name',
    item: 'name',
    group: 'group',
    person: 'assignees',
    people: 'assignees',
    owner: 'assignees',
    status: 'status',
    priority: 'priority',
    date: 'dueDate',
    duedate: 'dueDate',
    deadline: 'dueDate',
    // Timeline cells are "start - end" ranges; left for the value step.
    timeline: null,
    subitems: null,
    itemid: null,
    lastupdated: null,
    creationlog: null,
    notes: 'description',
    text: 'description',
    tags: 'tags',
  },
  externalIdColumn: 'itemid',
  headersVerified: false,
  exportHint:
    'In monday.com, open the board menu (⋯) → More actions → Export board to Excel. Leave "Include subitems" off.',
  notes: [
    'Board groups become task groups.',
    'Subitems are not imported in this version — export without them.',
  ],
}

/** Accelo → Companies list → Export (CSV emailed to you). */
export const ACCELO_COMPANIES_PRESET: ImportSourcePreset = {
  id: 'accelo-companies',
  sourceSystem: 'ACCELO',
  sourceLabel: 'Accelo',
  exportLabel: 'companies list',
  moduleType: ModuleTypeEnum.ENTITY,
  fingerprint: {
    requireAll: [['company', 'companyname'], ['standing']],
    distinctive: ['standing', 'manager', 'dateadded', 'companystatus'],
  },
  columns: {
    id: null,
    company: 'name',
    companyname: 'name',
    standing: 'lifecycleStage',
    companystatus: null,
    manager: null,
    phone: 'phone',
    website: 'website',
    dateadded: null,
    datelastinteracted: null,
    comments: 'description',
  },
  externalIdColumn: 'id',
  headersVerified: false,
  exportHint:
    'In Accelo, open Companies → list view, choose the columns you want, then Export. Accelo emails you the CSV.',
  notes: ['Standing (Active, Prospect…) maps to the lifecycle stage.'],
}

/** Accelo → Projects (Jobs) list → Export. Jobs become projects. */
export const ACCELO_JOBS_PRESET: ImportSourcePreset = {
  id: 'accelo-jobs',
  sourceSystem: 'ACCELO',
  sourceLabel: 'Accelo',
  exportLabel: 'projects list',
  moduleType: ModuleTypeEnum.PROJECT,
  fingerprint: {
    // Accelo renamed Jobs to Projects in its UI; older exports still say Job.
    // "Project" alone is far too common, so an Accelo-only column is required.
    requireAll: [
      ['job', 'jobtitle', 'jobname', 'project', 'projecttitle'],
      ['against', 'jobtype', 'datestarted'],
    ],
    distinctive: [
      'job',
      'jobtype',
      'jobstatus',
      'datestarted',
      'datedue',
      'against',
    ],
  },
  columns: {
    id: null,
    job: 'name',
    jobtitle: 'name',
    jobname: 'name',
    project: 'name',
    projecttitle: 'name',
    company: 'entityId',
    against: 'entityId',
    client: 'entityId',
    jobtype: null,
    projecttype: null,
    status: 'status',
    jobstatus: 'status',
    datestarted: 'startDate',
    datedue: 'endDate',
    manager: null,
    description: 'description',
  },
  externalIdColumn: 'id',
  headersVerified: false,
  exportHint:
    'In Accelo, open Projects → list view, then Export. Import Accelo companies first so projects link to their client.',
}

/** Accelo → Tasks list → Export. */
export const ACCELO_TASKS_PRESET: ImportSourcePreset = {
  id: 'accelo-tasks',
  sourceSystem: 'ACCELO',
  sourceLabel: 'Accelo',
  exportLabel: 'tasks list',
  moduleType: ModuleTypeEnum.TASK,
  fingerprint: {
    requireAll: [['task', 'title', 'taskname'], ['against']],
    distinctive: ['against', 'datedue', 'datestarted', 'assignee'],
  },
  columns: {
    id: null,
    task: 'name',
    title: 'name',
    taskname: 'name',
    // Tasks import into the project chosen in the wizard.
    against: null,
    assignee: 'assignees',
    status: 'status',
    priority: 'priority',
    datedue: 'dueDate',
    datestarted: null,
    description: 'description',
    manager: null,
  },
  externalIdColumn: 'id',
  headersVerified: false,
  exportHint:
    'In Accelo, filter the Tasks list to one project, then Export. Import each project’s tasks into that project.',
}

/** Scoro → Contacts → select → Export (CSV or XLS). */
export const SCORO_CONTACTS_PRESET: ImportSourcePreset = {
  id: 'scoro-contacts',
  sourceSystem: 'SCORO',
  sourceLabel: 'Scoro',
  exportLabel: 'contacts list',
  moduleType: ModuleTypeEnum.CONTACT,
  fingerprint: {
    requireAll: [['name', 'firstname', 'fullname'], ['contacttype']],
    distinctive: ['contacttype', 'regno', 'vatno', 'manager'],
  },
  columns: {
    id: null,
    name: 'name',
    fullname: 'name',
    firstname: 'firstName',
    lastname: 'lastName',
    contacttype: null,
    email: 'email',
    phone: 'phone',
    company: 'entity',
    position: 'title',
    manager: null,
    regno: null,
    vatno: null,
  },
  externalIdColumn: 'id',
  headersVerified: false,
  exportHint:
    'In Scoro, filter Contacts to Type = Person, select them, then Export. Export companies separately into Clients.',
  notes: [
    'Filter to people before exporting — company rows would import as people.',
  ],
}

/** Scoro → Projects → select → Export. */
export const SCORO_PROJECTS_PRESET: ImportSourcePreset = {
  id: 'scoro-projects',
  sourceSystem: 'SCORO',
  sourceLabel: 'Scoro',
  exportLabel: 'projects list',
  moduleType: ModuleTypeEnum.PROJECT,
  fingerprint: {
    requireAll: [['projectname', 'project', 'name'], ['projectno']],
    distinctive: ['projectno', 'projectmanager', 'deadline'],
  },
  columns: {
    projectno: null,
    projectname: 'name',
    project: 'name',
    name: 'name',
    company: 'entityId',
    status: 'status',
    startdate: 'startDate',
    deadline: 'endDate',
    duedate: 'endDate',
    projectmanager: null,
    description: 'description',
  },
  externalIdColumn: 'projectno',
  headersVerified: false,
  exportHint:
    'In Scoro, open Projects, select the projects, then Export. Import Scoro companies into Clients first so projects link up.',
}

/** Scoro → Tasks → select → Export. */
export const SCORO_TASKS_PRESET: ImportSourcePreset = {
  id: 'scoro-tasks',
  sourceSystem: 'SCORO',
  sourceLabel: 'Scoro',
  exportLabel: 'tasks list',
  moduleType: ModuleTypeEnum.TASK,
  fingerprint: {
    requireAll: [
      ['taskname', 'task', 'title'],
      ['activitytype', 'activity'],
    ],
    distinctive: ['activitytype', 'assignedto', 'deadline', 'timeplanned'],
  },
  columns: {
    id: null,
    taskname: 'name',
    task: 'name',
    title: 'name',
    project: null,
    projectname: null,
    activitytype: 'taskType',
    activity: 'taskType',
    assignedto: 'assignees',
    status: 'status',
    priority: 'priority',
    deadline: 'dueDate',
    timeplanned: null,
    description: 'description',
  },
  externalIdColumn: 'id',
  headersVerified: false,
  exportHint:
    'In Scoro, filter Tasks to one project, select them, then Export. Import each project’s tasks into that project.',
}

export const WORK_SOURCE_PRESETS: readonly ImportSourcePreset[] = [
  MONDAY_BOARD_TASKS_PRESET,
  ACCELO_COMPANIES_PRESET,
  ACCELO_JOBS_PRESET,
  ACCELO_TASKS_PRESET,
  SCORO_CONTACTS_PRESET,
  SCORO_PROJECTS_PRESET,
  SCORO_TASKS_PRESET,
]
